<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import LandingScreen from '../master/LandingScreen.vue';
import { BASE_URL } from '@/config';
import { useI18n } from 'vue-i18n';

const allProjects = ref([]);
const selectedProject = ref(null);
const selectedProjectId = ref(null);
const selectedImage = ref(null);
const isFullscreen = ref(false);
const isSidebarVisible = ref(false);
const screenWidth = ref(window.innerWidth);
const loading = ref(true);

const route = useRoute();
const router = useRouter();
const { locale, t } = useI18n();

// متغيرات حالة تحميل الصور
const isMainImageLoading = ref(true);
const thumbnailImageLoading = ref({});

// تحديث عرض الشاشة عند تغيير الحجم
window.addEventListener('resize', () => {
    screenWidth.value = window.innerWidth;
});

// دالة لجلب المشاريع
async function fetchProjects() {
    loading.value = true;
    try {
        const response = await fetch(`${BASE_URL}/projects`, {
            headers: {
                'Accept-Language': locale.value,
            },
        });
        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const data = await response.json();

        // التحقق من أن data.data هي مصفوفة
        if (!Array.isArray(data.data)) {
            throw new Error('Invalid data format: data.data is not an array');
        }

        allProjects.value = data.data;
        console.log('Fetched projects:', allProjects.value);
    } catch (error) {
        console.error('Error fetching projects:', error);
        allProjects.value = []; // تعيين مصفوفة فارغة في حالة الخطأ
    } finally {
        loading.value = false;
    }
}

// دالة لجلب تفاصيل مشروع محدد
async function goToProject(projectId) {
    console.log('Fetching project details for ID:', projectId);
    selectedProjectId.value = projectId;
    loading.value = true;
    try {
        const response = await fetch(`${BASE_URL}/projects/${projectId}`, {
            headers: {
                'Accept-Language': locale.value,
            },
        });
        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const data = await response.json();

        // التحقق من أن data.data هو كائن
        if (!data.data || typeof data.data !== 'object') {
            throw new Error('Invalid data format: data.data is not an object');
        }

        selectedProject.value = data.data;
        selectedImage.value = selectedProject.value.images ? selectedProject.value.images[0] : null;
        router.push(`/projects/${projectId}`);
        console.log('Selected project:', selectedProject.value);

        // تهيئة حالة تحميل الصورة الرئيسية
        isMainImageLoading.value = true;

        // تهيئة حالات تحميل الصور المصغرة
        thumbnailImageLoading.value = {};
        if (selectedProject.value.images) {
            selectedProject.value.images.forEach((image) => {
                thumbnailImageLoading.value[image] = true;
            });
        }
    } catch (error) {
        console.error('Error fetching project details:', error);
        selectedProject.value = null; // تعيين null في حالة الخطأ
    } finally {
        loading.value = false;
        isSidebarVisible.value = false;
    }
}

// إضافة مستمع للوحة المفاتيح عند تحميل المكون
onMounted(() => {
    window.addEventListener('keydown', handleKeyPress);
});

// إزالة المستمع عند تدمير المكون لتجنب تسرب الذاكرة
onUnmounted(() => {
    window.removeEventListener('keydown', handleKeyPress);
});

// دالة التعامل مع ضغطات المفاتيح
function handleKeyPress(event) {
    if (event.key === 'Escape' && isFullscreen.value) {
        toggleFullscreen(); // الخروج من وضع الشاشة الكاملة عند الضغط على Esc
    }
}

function toggleSidebar() {
    isSidebarVisible.value = !isSidebarVisible.value;
}

function selectImage(image) {
    if (image) {
        selectedImage.value = image;
        isMainImageLoading.value = true;
    } else {
        console.error('Selected image is null or undefined');
    }
}

function toggleFullscreen() {
    isFullscreen.value = !isFullscreen.value;
}

function prevImage() {
    if (!selectedProject.value || !selectedProject.value.images) return;
    const index = selectedProject.value.images.indexOf(selectedImage.value);
    if (index > 0) {
        selectedImage.value = selectedProject.value.images[index - 1];
        isMainImageLoading.value = true;
    }
}

function nextImage() {
    if (!selectedProject.value || !selectedProject.value.images) return;
    const index = selectedProject.value.images.indexOf(selectedImage.value);
    if (index < selectedProject.value.images.length - 1) {
        selectedImage.value = selectedProject.value.images[index + 1];
        isMainImageLoading.value = true;
    }
}

// جلب المشاريع عند تحميل الصفحة
onMounted(async () => {
    await fetchProjects();
    const projectId = route.params.id;
    if (projectId) {
        await goToProject(projectId);
    }
});

// مراقبة تغيير معرف المشروع في الرابط
watch(() => route.params.id, (newId) => {
    if (newId) {
        goToProject(newId);
    }
});

// مراقبة تغيير اللغة
watch(locale, () => {
    fetchProjects();
    if (selectedProjectId.value) {
        goToProject(selectedProjectId.value);
    }
});
</script>

<template>
    <div class="flex">
        <!-- زر فتح وإغلاق الـ Sidebar -->
        <button @click="toggleSidebar" class="custom-btn" :class="{ 'is-sidebar-closed': !isSidebarVisible }">
            <div class="flex flex-col items-center rounded">
                <font-awesome-icon icon="fa-solid fa-building" class="fa-icon" />
                <p class="hidden md:block">{{ t('projects.title') }}</p>
            </div>
        </button>

        <!-- Sidebar لعرض قائمة المشاريع -->
        <div
            :class="['fixedSidebar top-0 left-0 h-full bg-white dark:bg-secondaryDark text-white p-4 transition-transform',
                { 'transform translate-x-0': isSidebarVisible, 'transform -translate-x-full': !isSidebarVisible }
            ]">
            <h2 class="text-4xl font-bold my-10 text-center text-primary">{{ t('projects.title') }}</h2>
            <nav class="space-y-4">
                <button v-for="project in allProjects" :key="project.id" @click="goToProject(project.id)"
                    :class="['w-full p-4 rounded-lg text-center bg-secondary text-xl opacity-45',
                        selectedProjectId === project.id ?
                        'bg-gray-400 text-white' :
                        'bg-gray-700 hover:bg-gray-600'
                    ]">
                    {{ project.name }}
                </button>
            </nav>
        </div>

        <!-- Container يعرض تفاصيل المشروع -->
        <div class="flex-1 p-4 lg:p-8 mt-8 lg:mt-18">
            <!-- حالة التحميل -->
            <div v-if="loading" class="flex justify-center items-center h-full">
                <div class="text-xl font-semibold">
                    <LandingScreen />
                </div>
            </div>

            <!-- تفاصيل المشروع عند توفرها -->
            <div v-else-if="selectedProject"
                class="project-box mx-auto p-6 bg-white dark:bg-secondaryDark rounded-lg shadow-md max-w-4xl lg:max-w-6xl">
                <h2 class="text-3xl lg:text-4xl font-bold text-[#B99269] text-center mb-16">{{ selectedProject.name }}</h2>

                <div class="flex flex-col lg:flex-row gap-4 lg:gap-8 items-center">
                    <!-- Main Image -->
                    <div class="flex-1 relative">
                        <div class="cursor-pointer" @click="toggleFullscreen">
                            <!-- عنصر تحميل الصورة الرئيسية -->
                            <div v-if="isMainImageLoading"
                                class="rounded-md h-12 w-12 border-4 border-t-4 border-secondary animate-spin  ">
                            </div>

                            <!-- صورة المشروع الرئيسية -->
                            <img v-if="selectedImage" :src="selectedImage" loading="lazy" :alt="selectedProject.name"
                                class="w-full h-[300px] lg:h-[500px] object-cover rounded-lg shadow-lg"
                                @load="isMainImageLoading = false" @error="isMainImageLoading = false"
                                @loadstart="isMainImageLoading = true" />
                        </div>

                        <!-- الصور المصغرة -->
                        <div class="mt-4 flex flex-wrap justify-center gap-2">
                            <div v-for="(image, index) in selectedProject.images" :key="index" class="relative">
                                <!-- عنصر تحميل الصورة المصغرة -->
                                <div v-if="thumbnailImageLoading[image]"
                                    class="rounded-md h-10 w-10 border-4 border-t-4 border-secondary animate-spin absolute top-0 left-0 flex items-center justify-center">
                                </div>

                                <!-- الصورة المصغرة -->
                                <img :src="image" :alt="`${selectedProject.name} - ${index + 1}`"
                                    @click="selectImage(image)"
                                    :class="{ 'border-2 border-[#B99269]': selectedImage === image }"
                                    class="w-16 h-16 lg:w-24 lg:h-24 object-cover rounded-md cursor-pointer transition-transform transform hover:scale-105"
                                    loading="lazy"
                                    @load="thumbnailImageLoading[image] = false"
                                    @error="thumbnailImageLoading[image] = false"
                                    @loadstart="thumbnailImageLoading[image] = true" />
                            </div>
                        </div>
                    </div>

                    <!-- Project Description -->
                    <div class="flex-1 flex-col max-w-lg">
                        <p class="dark:text-white leading-relaxed mb-6">{{ selectedProject.description }}</p>

                        <div class="flex flex-col gap-4">
                            <hr class="border-b border-[#6F7782] dark:border-yellow-500 w-full mb-4">
                            <div class="flex flex-row lg:flex-row justify-between" style="align-items: normal;">
                                <div class="text-center">
                                    <h3 class="text-lg dark:text-white font-semibold opacity-75">
                                        {{ t('projects.start') }}</h3>
                                    <p class="text-sm dark:text-white">{{ selectedProject.starting_date }}</p>
                                </div>
                                <div class="text-center">
                                    <h3 class="text-lg dark:text-white font-semibold opacity-75">{{ t('projects.end') }}
                                    </h3>
                                    <p class="text-sm dark:text-white">{{ selectedProject.ending_date }}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Fullscreen Image View -->
            <div v-if="isFullscreen" class="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center"
                style="z-index: 1000;" @click.self="toggleFullscreen">
                <img :src="selectedImage" alt="Fullscreen Image"
                    class="max-w-[95%] max-h-[95%] rounded-lg shadow-lg" />
                <button class="absolute top-4 right-4 bg-transparent text-white text-3xl font-bold"
                    @click="toggleFullscreen">×</button>
                <button
                    class="absolute top-1/2 left-4 transform -translate-y-1/2 bg-black bg-opacity-70 text-white text-3xl font-bold px-2 py-1"
                    @click="prevImage" style="direction: ltr;">❮</button>
                <button
                    class="absolute top-1/2 right-4 transform -translate-y-1/2 bg-black bg-opacity-70 text-white text-3xl font-bold px-2 py-1"
                    @click="nextImage" style="direction: ltr;">❯</button>
            </div>

            <!-- رسالة في حال عدم وجود مشروع محدد -->
            <div v-else-if="!loading && !selectedProject" class="text-center my-8">
                <p class="text-gray-600">No project selected.</p>
            </div>
        </div>
    </div>
</template>

<style scoped>
/* Fullscreen Image View */
.fullscreen-image {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 999999999999999;
    background-color: rgba(0, 0, 0, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;
}

.fullscreen-image img {
    max-width: 95%;
    max-height: 95%;
    border-radius: 8px;
    box-shadow: 0 0 15px rgba(0, 0, 0, 0.5);
}

/* Sidebar تنسيقات */
.fixedSidebar {
    position: fixed;
    top: 11.5%;
    left: 0;
    height: 90vh;
    width: 16rem;
    box-shadow: 2px 0 5px rgba(0, 0, 0, 0.1);
    transition: transform 0.3s ease;
    z-index: 50;
    overflow-y: auto;
    --sb-track-color: #34505d;
    --sb-thumb-color: #314351;
    --sb-size: 5px;
}

.fixedSidebar::-webkit-scrollbar {
    width: var(--sb-size);
}

.fixedSidebar::-webkit-scrollbar-track {
    background: var(--sb-track-color);
    border-radius: 10px;
}

.fixedSidebar::-webkit-scrollbar-thumb {
    background: var(--sb-thumb-color);
    border-radius: 10px;
    border: 8px solid #c7a887;
}

@supports not selector(::-webkit-scrollbar) {
    .fixedSidebar {
        scrollbar-color: var(--sb-thumb-color) var (--sb-track-color);
    }
}

:root {
    --sb-track-color: #c7a887;
    --sb-thumb-color: #314351;
    --sb-size: 5px;
}

/* زر فتح وإغلاق الـ Sidebar */
button {
    border: none;
    cursor: pointer;
    padding: 0.5rem 0.75rem;
}

button:hover {
    opacity: 0.9;
}

button.is-sidebar-closed {
    padding: 0.5rem;
    position: fixed;
    top: 15%;
    left: 4%;
    z-index: 100;
    background-color: #BB936A;
    color: white;
    border-radius: 20px;
    text-align: center;
}

.project-box {
    box-shadow: 1px 3px 35px 0px #293340;
    padding: 20px;
    margin-top: 100px;
    margin-bottom: 50px;
}

.dark .project-box {
    box-shadow: 1px 3px 35px 0px #BB936A;
}

button.active {
    background-color: #4a4a4a;
    color: white;
}

.custom-btn {
    position: fixed;
    top: 16px;
    left: 16px;
    width: 100px;
    height: 50px;
    border-radius: 8px;
    font-size: 18px;
    text-align: center;
    display: flex;
    justify-content: center;
    align-items: center;
    color: white;
    z-index: 50;
}

.fa-icon {
    font-size: 24px;
    margin-bottom: 5px;
}

/* شاشات الجوال */
@media (max-width: 768px) {
    .custom-btn {
        width: 50px;
        height: 50px;
    }

    .fa-icon {
        font-size: 20px;
    }
}

/* شاشات العرض الكبيرة */
@media (min-width: 768px) {
    .custom-btn {
        width: 120px;
        height: 70px;
        left: 16px;
        top: 16px;
    }

    .fa-icon {
        font-size: 28px;
    }
}

/* عنصر تحميل الصورة */
img {
    position: relative;
}

.relative {
    position: relative;
}

.absolute {
    position: absolute;
}
</style>
